/**
 * The single alert line (top centre): only what is urgent, one thing at a time, by priority —
 *   fresh order feedback (< 1.2 s) > our own inbound (a warhead on our critical capital always
 *   first) > CAPITAL CRITICAL (persistent while the window runs) > a calm open-ground inbound > the
 *   rest of the feedback > the weapons-release countdown > a rival's critical capital we can
 *   strike (Q STRIKE) > an ally's city / capital / silo under a missile (teal, calm) > SPECTATING /
 *   first-match tips / intro.
 * What we must act on or care about otherwise goes to the toasts (`state/toasts.ts`, mapped in
 * `state/news.ts`); offers to us are pinned toasts. Our own orders are confirmed by the pointer
 * (`Hud.flash`), not here and not in the news.
 * Pure apart from the clock passed in (tested in test/alerts.test.ts).
 */
import { BALANCE, TICK_HZ, type EntityId, type EntityView, type PlayerId, type PlayerView } from '@sa/sim';
import { KEY, braceAction, type ChipAction } from '../input/actions';
import { fmtInt } from '../ui/dom';
import type { GameEvent, GameStore } from './store';
import { Waves, betrayalText, newsFor } from './news';
import { allyStakeMatters, type InboundThreat } from './threats';
import { Tips } from './tips';
import { ToastQueue } from './toasts';

/** `calm`: dim amber — worth knowing, nothing to lose (open-ground inbound, link trouble) */
export type AlertTone = 'danger' | 'warn' | 'calm' | 'info' | 'ally' | 'dim' | 'ok';

export interface AlertLine {
  text: string;
  tone: AlertTone;
  /** a trailing cue in its own tone, e.g. "NO COVER" (danger) or "BRACED 0:14" (ok) */
  tail?: { text: string; tone: AlertTone };
  /** what to do about it, as key + button: G · BRACE 300, T · FOCUS SAMS */
  actions?: ChipAction[];
  /** a one-time explanation under the line (first-match tips) */
  sub?: string;
  /** the calm, outlined treatment (an ally's ground under a missile) */
  outline?: boolean;
  /** the loud treatment: bigger, saturated (our capital is critical) */
  strong?: boolean;
}

const ERROR_MS = 2200;
/** an accepted offer's pact this soon after (ms) is not news (the accept was confirmed) */
const ACCEPTED_MS = 10_000;
const RELIEF_MS = 1500;
/** order feedback this young pre-empts even the inbound line */
const FRESH_MS = 1200;
const RELEASE_COUNTDOWN_TICKS = 300;
const INTRO_TICKS = 80;
/** the silo tip stays this long after weapons release (then waits for the next match) */
const SILO_TIP_TICKS = 600;
/** the last minute: the clock turns amber, no tips (ticks) */
export const FINAL_MINUTE_TICKS = 60 * TICK_HZ;
/** the last seconds get a big countdown (ticks) */
export const FINAL_COUNT_TICKS = 10 * TICK_HZ;

/** banked credits above this (after release) light the credits box (`.bank`) */
export const BANK_CREDITS = 10_000;

export const TIP_TEXT = {
  push: 'RIGHT-CLICK LAND TO PUSH YOUR BORDER',
  silo: '3 · BUILD A SILO TO STRIKE BACK',
  brace: 'R · BRACE — CITY TAKES NO DAMAGE FOR 20 S',
  critical: 'ONE MORE HIT ELIMINATES YOU — BRACE OR INTERCEPT',
  diplo: 'HOLD RIGHT-CLICK ON A NATION FOR DIPLOMACY',
} as const;

/** the diplomacy tip waits this long into the match (after the push tip is done), until weapons release */
const DIPLO_TIP_TICKS = 600;

export function clock(secs: number): string {
  const s = Math.max(0, Math.ceil(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * A moment of the match as the HUD clock showed it: `9:54 LEFT`. The clock counts down, so every
 * m:ss on screen is a countdown (`0:24`, `T-0:11`) or this — never elapsed time.
 */
export function leftAt(tick: number, matchTicks: number = BALANCE.matchTicks): string {
  return `${clock((matchTicks - tick) / TICK_HZ)} LEFT`;
}

/**
 * "INBOUND ×2 · 120 KM N OF PARIS · CAPITAL · 0:18" (+ "NO COVER" without a SAM on it), for our most urgent threat
 * that puts something at stake, and — when it falls on one of our cities and `view` is given —
 * the brace offer for that city. Red only while there is something to lose: a city braced
 * through the impact turns the line amber with a green `BRACED 0:14`, and warheads that only
 * hit open ground are one calm amber line (`INBOUND · OPEN GROUND · 0:20`, `calm`).
 */
export function inboundLine(threats: readonly InboundThreat[], view?: PlayerView, braceArms?: ReadonlySet<EntityId>): (AlertLine & { calm?: boolean }) | null {
  if (!threats.length) return null;
  const n = threats.length;
  const count = `INBOUND${n > 1 ? ` ×${n}` : ''}`;
  const t = threats.find((x) => x.stake);
  if (!t) return { text: `${count} · OPEN GROUND · ${clock(threats[0].etaSeconds)}`, tone: 'calm', calm: true };
  // a warhead on our critical capital needs no place name: "INBOUND ×2 · CAPITAL CRITICAL · 0:17"
  const parts = t.critical ? [count, 'CAPITAL CRITICAL'] : [count, t.label];
  if (!t.critical && t.stake && t.stake !== t.label) parts.push(t.stake);
  parts.push(clock(t.etaSeconds));
  if (t.bracedSeconds !== null) return { text: parts.join(' · '), tone: 'warn', tail: { text: `BRACED ${clock(t.bracedSeconds)}`, tone: 'ok' } };
  // cover is only worth words when it is missing (actionable: build a SAM, or brace); the
  // covering SAMs' pulsing rings show it when it is there
  const line: AlertLine = { text: parts.join(' · '), tone: 'danger' };
  if (!t.samCovered) line.tail = { text: 'NO COVER', tone: 'danger' };
  // an armed brace (G pressed early) says when it starts: still red, nothing is braced yet
  const armed = t.cityId !== null && braceArms?.has(t.cityId) ? braceTiming(threats, t.cityId) : null;
  if (armed) {
    line.tail = { text: `BRACE ARMED · ACTIVE AT T-${clock(armed.activeAt)}`, tone: 'ok' };
    return line;
  }
  const city = view && t.cityId !== null ? view.entities.get(t.cityId) : undefined;
  if (view && city) line.actions = [braceAction(view, city)];
  return line;
}

/** A brace lasts this long (s)… */
export const BRACE_S = BALANCE.brace.ticks / TICK_HZ;
/** …and an armed one is sent this long before the last impact it covers (1 s of slack for the clock and the link). */
export const BRACE_LEAD_S = BRACE_S - 1;
/** an armed brace that would go out sooner than this is just sent (s) */
export const BRACE_NOW_S = 0.3;

/**
 * When to brace city `cityId` against the warheads on it (§1.15): `sendIn` seconds from now
 * (0: now) and the first impact's ETA at that moment (`activeAt`: "ACTIVE AT T-0:19"). A brace
 * lasts 20 s, so pressed early it is armed: sent just in time to cover the first impact and
 * every later one up to 18 s after it (a salvo). Null when nothing unbraced is on its way there.
 */
export function braceTiming(threats: readonly InboundThreat[], cityId: EntityId): { sendIn: number; activeAt: number } | null {
  const etas = threats.filter((t) => t.cityId === cityId && t.bracedSeconds === null).map((t) => t.etaSeconds);
  if (!etas.length) return null;
  const first = Math.min(...etas);
  const last = Math.max(...etas.filter((e) => e <= first + BRACE_LEAD_S - 1));
  const sendIn = Math.max(0, last - BRACE_LEAD_S);
  return { sendIn, activeAt: first - sendIn };
}

/** The threat the inbound line is about (the most urgent one with a stake, else the most urgent). */
export function namedThreat(threats: readonly InboundThreat[]): InboundThreat | undefined {
  return threats.find((t) => t.stake) ?? threats[0];
}

/**
 * Our capital is critical (§1.11): "CAPITAL CRITICAL · 1:24" for as long as the window runs, with
 * where the brace stands as its tail — `R BRACE READY`, `BRACED 0:12` (the line turns amber: a hit
 * now costs nothing), `BRACE 1:12` (cooling down) or what it costs. Null when it is not critical.
 */
export function criticalLine(view: PlayerView, capital: EntityView | undefined, tick: number): AlertLine | null {
  const until = view.criticalCapitals.get(view.me);
  if (until === undefined || view.eliminated || view.result) return null;
  const text = `CAPITAL CRITICAL · ${clock((until - tick) / TICK_HZ)}`;
  if (!capital) return { text, tone: 'danger', strong: true };
  const b = braceAction(view, capital);
  if (b.done) return { text, tone: 'warn', strong: true, tail: { text: b.label, tone: 'ok' } };
  const tail = b.enabled ? 'R BRACE READY' : b.why?.startsWith('BRACE READY IN') ? b.label : `BRACE ${fmtInt(BALANCE.brace.cost)} · NO CREDITS`;
  return { text, tone: 'danger', strong: true, tail: { text: tail, tone: b.enabled ? 'ok' : 'dim' } };
}

/**
 * "ALLY SPECTRE · SEATTLE · CITY L3 · 0:24": an ally's city, capital or silo under a missile —
 * teal, no cover cue, no alarm. Their open ground gets no line (only the small teal ring).
 */
export function allyLine(threats: readonly InboundThreat[], nameOf: (p: PlayerId) => string): AlertLine | null {
  const staked = threats.filter(allyStakeMatters);
  const t = staked[0];
  if (!t) return null;
  const stake = t.stake!.replace(/^ALLY /, '');
  const parts = [`ALLY ${nameOf(t.landOwner)}${staked.length > 1 ? ` ×${staked.length}` : ''}`, t.label];
  if (stake !== t.label) parts.push(stake);
  parts.push(clock(t.etaSeconds));
  return { text: parts.join(' · '), tone: 'ally', outline: true };
}

/**
 * A rival's capital is critical and our launchers reach it: "VULCAN CAPITAL CRITICAL · 1:10 · ONE HIT
 * ELIMINATES" with `Q STRIKE` (every launcher that reaches it, aiming at it).
 */
export function rivalCriticalLine(name: string, secsLeft: number, strike: () => void): AlertLine {
  return { text: `${name} CAPITAL CRITICAL · ${clock(secsLeft)} · ONE HIT ELIMINATES`, tone: 'warn', actions: [{ key: KEY.attack, label: 'STRIKE', enabled: true, run: strike }] };
}

/** The threatened city G braces right now (the most urgent threat that falls on a city we can brace and have not armed), or null. */
export function braceCandidate(threats: readonly InboundThreat[], view: PlayerView, armed?: ReadonlySet<EntityId>): ChipAction | null {
  for (const t of threats) {
    const city = t.cityId !== null && t.bracedSeconds === null && !armed?.has(t.cityId) ? view.entities.get(t.cityId) : undefined;
    if (city) return braceAction(view, city);
  }
  return null;
}

/** The intercept relief line: the whole salvo down → "INTERCEPTED · DELHI SAFE", else "INTERCEPTED · 1 OF 2". */
export function reliefText(label: string, stake: string | null, down: number, of: number): string {
  if (down < of) return `INTERCEPTED · ${down} OF ${of}`;
  // a place name reads "DELHI SAFE"; a bearing ("380 KM NE OF DELHI") is named by its stake
  const place = / OF |^NEAR /.test(label) ? stake : label;
  return place ? `INTERCEPTED · ${place} SAFE` : 'INTERCEPTED';
}

/**
 * How one of our strikes ended (every missile down): `KAZAN · 2 OF 3 THROUGH`, `KAZAN · INTERCEPTED ×2`,
 * `KAZAN · HIT`, or — impacts outside our sensors — `KAZAN · IMPACTS UNSEEN` / `… · 2 OF 3 THROUGH · UNSEEN`.
 * Warheads that landed after the target nation was already out are wasted, as the results count
 * them: `SAM · KAZAN · ALREADY OUT · 12 WASTED`, `KAZAN · 4 OF 12 THROUGH · 3 WASTED`.
 */
export function strikeText(label: string, n: number, through: number, seen: boolean, wasted = 0): string {
  if (wasted > 0 && wasted >= through) return `${label} · ALREADY OUT · ${wasted} WASTED`;
  if (wasted > 0) return `${strikeText(label, n, through, seen)} · ${wasted} WASTED`;
  if (!through) return `${label} · INTERCEPTED${n > 1 ? ` ×${n}` : ''}`;
  if (through < n) return `${label} · ${through} OF ${n} THROUGH${seen ? '' : ' · UNSEEN'}`;
  if (!seen) return `${label} · IMPACT${n > 1 ? 'S' : ''} UNSEEN`;
  return n > 1 ? `${label} · ${n} OF ${n} THROUGH` : `${label} · HIT`;
}

/** Two impacts closer than this many hex radii (×√3) hit the same target (one salvo). */
export const SAME_TARGET_HEXES = 1.5;

/** A short line of order feedback (a refusal, a confirmation, the intercept relief). */
interface Feedback extends AlertLine {
  bornMs: number;
  ms: number;
}

export class Alerts {
  /** the news of the match (`state/toasts.ts`): what is not urgent leaves the line for these */
  readonly toasts = new ToastQueue();
  private feedback: Feedback | null = null;
  /** the threat our inbound line named last and its salvo (for the intercept relief beat) */
  private named: { id: EntityId; label: string; stake: string | null; salvo: EntityId[]; down: number } | null = null;
  private wasCritical = false;
  /** a second action for the inbound line (SAM focus), set by the session each frame */
  extraAction: ChipAction | null = null;
  /** a rival's critical capital we can strike (`rivalCriticalLine`), set by the session */
  rivalLine: AlertLine | null = null;
  /** our losses and air fights, a card a wave */
  private waves: Waves;
  /** the first-match guide is up (`state/guide.ts`): its steps say what the push and silo tips would */
  guided = false;
  /** offers we accepted just now (`accepted`): their ALLIANCE / CEASEFIRE is no news to us */
  private acceptedMs = new Map<string, number>();

  constructor(
    private store: GameStore,
    private nameOf: (p: PlayerId) => string,
    private matchTicks: number = BALANCE.matchTicks,
    readonly tips: Tips = new Tips(),
  ) {
    this.waves = new Waves(nameOf);
  }

  /** We accepted `from`'s offer (confirmed by the pointer): the pact's own news would only repeat it. */
  accepted(kind: 'ally' | 'ceasefire', from: PlayerId, nowMs: number): void {
    this.acceptedMs.set(`${kind}:${from}`, nowMs);
  }

  /** Feed a store event: its news becomes a toast (`newsFor`); an intercept may close the fear on the line. */
  onEvent(e: GameEvent, nowMs: number): void {
    if (e.type === 'intercept') {
      // relief: a missile of the salvo our line was about is down (SAFE only once all of it is)
      const n = this.named;
      if (!e.hit || !n || !n.salvo.includes(e.targetId)) return;
      n.down++;
      // (a beat that briefly pre-empts the rest of the salvo's inbound line, like order feedback)
      this.say(reliefText(n.label, n.stake, n.down, n.salvo.length), 'ok', nowMs, RELIEF_MS);
      if (n.down >= n.salvo.length) this.named = null;
      return;
    }
    // our own elimination gets the full-screen moment instead (and nothing stale lingers on the line)
    if (e.type === 'eliminated' && e.player === this.store.me) this.feedback = null;
    // allied again: an unseen betrayal by them is old news
    if (e.type === 'ally' && e.on) {
      const old = betrayalText(this.nameOf(e.with));
      this.toasts.drop((t) => t.text === old);
    }
    if ((e.type === 'ally' || e.type === 'ceasefire') && e.on && nowMs - (this.acceptedMs.get(`${e.type}:${e.with}`) ?? -Infinity) < ACCEPTED_MS) return;
    const wave = this.waves.add(e, nowMs, (k) => this.toasts.has(k));
    if (wave) return this.toasts.upsert({ ...wave, key: wave.key! }, nowMs);
    const news = newsFor(e, this.store, this.nameOf);
    if (news) this.toasts.push(news, nowMs);
  }

  /** A short line of feedback on the alert line (`ms` on screen; the newest replaces the last). */
  say(text: string, tone: AlertTone, nowMs: number, ms = ERROR_MS): void {
    this.feedback = { text, tone, bornMs: nowMs, ms };
  }

  /** A refused order / invalid action: short, dim. */
  error(text: string, nowMs: number): void {
    this.say(text, 'dim', nowMs);
  }

  /** A warning about the player's own action (friendly-fire confirmation). */
  warn(text: string, nowMs: number, ms = ERROR_MS): void {
    this.say(text, 'warn', nowMs, ms);
  }

  /** Confirmation of an order that is not visible at once (e.g. SAM focus). */
  confirm(text: string, nowMs: number): void {
    this.say(text, 'info', nowMs);
  }

  /** Tips are done by doing the thing once (or once they had their 20 s: `Tips.show`). */
  private updateTips(v: PlayerView): void {
    const tips = this.tips;
    if (v.eliminated) return;
    if (v.focus !== null) {
      tips.complete('push');
      tips.complete('pushed');
    }
    let silo = false;
    let braced = false;
    for (const e of v.entities.values()) {
      if (e.owner !== v.me) continue;
      // (a sub or a bomber strikes back too)
      if (e.kind === 'silo' || e.kind === 'sub' || e.kind === 'bomber') silo = true;
      else if (e.kind === 'city' && e.state === 'braced') braced = true;
    }
    if (silo) tips.complete('silo');
    if (braced) tips.complete('brace');
    // the critical tip is for the first time it happens: done once that window is over
    const critical = v.criticalCapitals.has(v.me);
    if (this.wasCritical && !critical) tips.complete('critical');
    this.wasCritical = critical;
  }

  /** A first-match tip that applies right now and still has its time (`Tips.show`), or null. */
  private tip(v: PlayerView, tick: number, nowMs: number): string | null {
    const tips = this.tips;
    if (v.eliminated || this.finalMinute(tick)) return null;
    if (!this.guided && tips.pending('push') && tips.show('push', nowMs)) return TIP_TEXT.push;
    if (!this.guided && tips.pending('silo') && v.released && tick < BALANCE.phases.releaseTick + SILO_TIP_TICKS && tips.show('silo', nowMs)) return TIP_TEXT.silo;
    if (tips.pending('diplo') && !v.released && tick >= DIPLO_TIP_TICKS && tips.show('diplo', nowMs)) return TIP_TEXT.diplo;
    return null;
  }

  /** The line to show now (see the file comment for the order). Call once per frame. */
  current(tick: number, nowMs: number): AlertLine | null {
    const v = this.store.view;
    this.updateTips(v);
    if (this.feedback && nowMs - this.feedback.bornMs >= this.feedback.ms) this.feedback = null;
    const line = this.pick(tick, nowMs, v);
    // a spectator — or anyone once the match is over — is offered no orders: no buttons, no keys
    return line?.actions && (v.eliminated || v.result) ? { ...line, actions: undefined } : line;
  }

  private pick(tick: number, nowMs: number, v: PlayerView): AlertLine | null {
    const fb = this.feedback;
    if (fb && nowMs - fb.bornMs < FRESH_MS) return fb;
    const own = this.store.inboundThreats;
    const inbound = inboundLine(own, v.eliminated ? undefined : v, this.store.braceArms);
    if (inbound) this.name(own);
    if (inbound && !inbound.calm) {
      const brace = inbound.actions?.[0];
      if (own[0].critical && this.tips.show('critical', nowMs)) inbound.sub = TIP_TEXT.critical;
      else if (brace?.enabled && this.tips.show('brace', nowMs)) inbound.sub = TIP_TEXT.brace;
      if (this.extraAction) inbound.actions = [...(inbound.actions ?? []), this.extraAction];
      return inbound;
    }
    const critical = criticalLine(v, this.store.ownCapitalCity(), tick);
    if (critical) {
      if (this.tips.show('critical', nowMs)) critical.sub = TIP_TEXT.critical;
      return critical;
    }
    // warheads on open ground: worth a calm line (over older feedback)
    if (inbound) return inbound;
    if (fb) return fb;
    const rel = BALANCE.phases.releaseTick;
    if (!v.eliminated && !v.released && tick >= rel - RELEASE_COUNTDOWN_TICKS && tick < rel) return { text: `WEAPONS RELEASE · ${clock((rel - tick) / TICK_HZ)}`, tone: 'warn' };
    if (this.rivalLine && !v.eliminated) return this.rivalLine;
    const ally = allyLine(this.store.allyThreats, this.nameOf);
    if (ally) return ally;
    if (v.eliminated) return { text: `SPECTATING · ${clock((this.matchTicks - tick) / TICK_HZ)} LEFT`, tone: 'dim' };
    const tip = this.tip(v, tick, nowMs);
    if (tip) return { text: tip, tone: 'info' };
    if (tick < INTRO_TICKS) return { text: `EXPAND · WEAPONS RELEASE IN ${clock((rel - tick) / TICK_HZ)}`, tone: 'info' };
    return null;
  }

  /** The last minute of the match (`FINAL_MINUTE_TICKS`) has begun. */
  finalMinute(tick: number): boolean {
    return tick >= this.matchTicks - FINAL_MINUTE_TICKS;
  }

  /** Remember the threat the inbound line names, and its salvo (same target), for the relief beat. */
  private name(own: readonly InboundThreat[]): void {
    const t = namedThreat(own)!;
    const same = SAME_TARGET_HEXES * Math.sqrt(3) * this.store.world.hexR;
    const salvo = own.filter((x) => (x.cityId !== null && x.cityId === t.cityId) || (x.target.x - t.target.x) ** 2 + (x.target.y - t.target.y) ** 2 <= same * same).map((x) => x.id);
    const n = this.named;
    // still the salvo we are counting (its first missile may be down already): keep the count
    if (n && (n.salvo.includes(t.id) || salvo.includes(n.id))) {
      n.id = t.id;
      for (const id of salvo) if (!n.salvo.includes(id)) n.salvo.push(id);
      return;
    }
    this.named = { id: t.id, label: t.label, stake: t.stake, salvo, down: 0 };
  }

  /** We never pushed a front in this browser yet: the in-world RMB · PUSH cue still applies. */
  get pushCue(): boolean {
    return this.tips.pending('pushed');
  }
}
